import * as React from 'react'
import * as Popover from '@radix-ui/react-popover'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parse,
  parseISO,
  startOfDay,
  startOfMonth,
  subMonths,
} from 'date-fns'
import { ar, enGB } from 'date-fns/locale'
import { useTranslation } from 'react-i18next'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { useCompanySettings } from '@/hooks/useCompanySettings'
import { cn } from '@/lib/utils'

/**
 * A drop-in replacement for `<input type="date">`.
 *
 * The native control drew its own calendar button on the trailing edge, which the pages
 * then doubled by overlaying an icon of their own — two calendar icons on one field —
 * and it opened the browser's calendar, whose format follows the browser's locale rather
 * than the app's. This owns both: one icon, one calendar, and it reads the direction and
 * month names from the app's language.
 *
 * The API is the native one: an ISO `yyyy-MM-dd` `value`, `onChange(event)` carrying
 * `event.target.value` (empty string when cleared), plus `min`/`max`/`required`/
 * `disabled`. Swapping an `<input type="date">` for it changes nothing about the page.
 */

const ISO = 'yyyy-MM-dd'

/**
 * The field is typed into, so it needs an unambiguous numeric format; `dd MMM yyyy` from
 * company settings is a display format and cannot be typed reliably. Callers that need
 * another one pass `displayFormat`.
 */
const DEFAULT_DISPLAY = 'dd/MM/yyyy'

export interface DatePickerChangeEvent {
  target: { value: string; name?: string }
}

export interface DatePickerProps {
  value?: string
  defaultValue?: string
  onChange?: (event: DatePickerChangeEvent) => void
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  name?: string
  id?: string
  min?: string | number
  max?: string | number
  disabled?: boolean
  required?: boolean
  readOnly?: boolean
  placeholder?: string
  className?: string
  invalid?: boolean
  displayFormat?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

function parseIso(raw: string | number | undefined): Date | undefined {
  if (!raw) return undefined
  const parsed = parseISO(String(raw))
  return isValid(parsed) ? startOfDay(parsed) : undefined
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  {
    value,
    defaultValue,
    onChange,
    onBlur,
    name,
    id,
    min,
    max,
    disabled,
    required,
    readOnly,
    placeholder,
    className,
    invalid,
    displayFormat = DEFAULT_DISPLAY,
    ...aria
  },
  ref,
) {
  const { i18n, t } = useTranslation()
  const { settings } = useCompanySettings()

  // Month and weekday names follow the language; the mirroring follows the document's
  // direction, which is where the app puts it.
  const locale = i18n.language.startsWith('ar') ? ar : enGB
  const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl'
  const weekStartsOn = ((settings?.weekStart ?? 1) % 7) as 0 | 1 | 2 | 3 | 4 | 5 | 6

  const [uncontrolled, setUncontrolled] = React.useState(defaultValue ?? '')
  const iso = value ?? uncontrolled
  const selected = parseIso(iso)

  const [open, setOpen] = React.useState(false)
  const [text, setText] = React.useState(() =>
    selected ? format(selected, displayFormat, { locale }) : '',
  )
  const [month, setMonth] = React.useState(() => startOfMonth(selected ?? new Date()))

  // The typed text follows the value while the field is not being edited, so a reset or a
  // programmatic change is reflected without the component owning two sources of truth.
  const focused = React.useRef(false)
  React.useEffect(() => {
    if (focused.current) return
    setText(selected ? format(selected, displayFormat, { locale }) : '')
    if (selected) setMonth(startOfMonth(selected))
    // `locale` is derived from i18n.language, which is the dependency that matters.
  }, [iso, displayFormat, i18n.language]) // eslint-disable-line react-hooks/exhaustive-deps

  const lower = parseIso(min)
  const upper = parseIso(max)
  const outOfRange = (day: Date) =>
    (lower !== undefined && day < lower) || (upper !== undefined && day > upper)

  function emit(next: string) {
    if (value === undefined) setUncontrolled(next)
    onChange?.({ target: { value: next, name } })
  }

  function commitTyped(raw: string) {
    const trimmed = raw.trim()
    if (trimmed === '') {
      emit('')
      return
    }
    // The display format first, then ISO. Somebody pasting 2026-03-01 out of a spreadsheet
    // or an email means that date, and snapping their paste back to blank because the field
    // wanted dd/MM/yyyy is the kind of small refusal people work around by giving up.
    let parsed = parse(trimmed, displayFormat, new Date(), { locale })
    if (!isValid(parsed)) {
      parsed = parse(trimmed, ISO, new Date(), { locale })
    }
    if (isValid(parsed) && !outOfRange(startOfDay(parsed))) {
      emit(format(parsed, ISO))
    } else {
      // Unparseable input snaps back rather than silently clearing a good value.
      setText(selected ? format(selected, displayFormat, { locale }) : '')
    }
  }

  function pick(day: Date) {
    emit(format(day, ISO))
    setText(format(day, displayFormat, { locale }))
    setOpen(false)
  }

  const gridStart = startOfMonth(month)
  const gridEnd = endOfMonth(month)
  const leading = (gridStart.getDay() - weekStartsOn + 7) % 7
  const days = eachDayOfInterval({ start: gridStart, end: gridEnd })
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    // date-fns weeks start on Sunday; rotate to the company's configured first day.
    locale.localize?.day(((index + weekStartsOn) % 7) as 0 | 1 | 2 | 3 | 4 | 5 | 6, {
      width: 'short',
    }),
  )
  const today = startOfDay(new Date())

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div
        className={cn(
          'relative flex h-10 w-full items-center rounded-[var(--radius-control)]',
          'border border-[var(--color-border)] bg-[var(--color-card)]',
          'focus-within:outline-2 focus-within:outline-offset-0 focus-within:outline-[var(--color-primary)]',
          disabled && 'cursor-not-allowed opacity-50',
          invalid && 'border-[var(--color-danger)]',
          className,
        )}
      >
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          data-slot="date-picker-input"
          id={id}
          name={name}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          aria-invalid={invalid || undefined}
          aria-label={aria['aria-label']}
          aria-labelledby={aria['aria-labelledby']}
          aria-describedby={aria['aria-describedby']}
          placeholder={placeholder ?? displayFormat.toLowerCase()}
          value={text}
          onFocus={() => {
            focused.current = true
          }}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            // Enter commits what has been typed. Leaving it to blur alone means somebody who
            // types a date and presses Enter — which is what people do — sees the field snap
            // back on the way out of a form they thought they had filled in.
            if (event.key === 'Enter') {
              commitTyped((event.target as HTMLInputElement).value)
            }
          }}
          onBlur={(event) => {
            focused.current = false
            commitTyped(event.target.value)
            onBlur?.(event)
          }}
          className={cn(
            'h-full min-w-0 flex-1 rounded-[inherit] bg-transparent px-3 text-sm',
            'text-[var(--color-heading)] placeholder:text-[var(--color-muted)]',
            'outline-none disabled:cursor-not-allowed',
          )}
        />
        {/* The one and only calendar affordance. A flex sibling, not an overlay, so it
            sits on the trailing edge under both directions. */}
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled || readOnly}
            aria-label={t('a11y.openCalendar', { defaultValue: 'Open calendar' })}
            className={cn(
              'me-1 grid size-8 shrink-0 place-items-center rounded-[6px] text-[var(--color-muted)]',
              'hover:bg-[var(--color-page)] hover:text-[var(--color-body)]',
              'focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]',
              'disabled:pointer-events-none',
            )}
          >
            <Calendar className="size-4" aria-hidden="true" />
          </button>
        </Popover.Trigger>
      </div>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={4}
          dir={rtl ? 'rtl' : 'ltr'}
          className={cn(
            'z-50 w-[17.5rem] rounded-[var(--radius-card)] border border-[var(--color-border)]',
            'bg-[var(--color-card)] p-3 shadow-[var(--shadow-popover)]',
          )}
        >
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setMonth(subMonths(month, 1))}
              aria-label={t('a11y.previousMonth', { defaultValue: 'Previous month' })}
              className="grid size-7 place-items-center rounded-[6px] text-[var(--color-body)] hover:bg-[var(--color-page)]"
            >
              {rtl ? (
                <ChevronRight className="size-4" aria-hidden="true" />
              ) : (
                <ChevronLeft className="size-4" aria-hidden="true" />
              )}
            </button>
            <span className="text-sm font-semibold text-[var(--color-heading)]">
              {format(month, 'MMMM yyyy', { locale })}
            </span>
            <button
              type="button"
              onClick={() => setMonth(addMonths(month, 1))}
              aria-label={t('a11y.nextMonth', { defaultValue: 'Next month' })}
              className="grid size-7 place-items-center rounded-[6px] text-[var(--color-body)] hover:bg-[var(--color-page)]"
            >
              {rtl ? (
                <ChevronLeft className="size-4" aria-hidden="true" />
              ) : (
                <ChevronRight className="size-4" aria-hidden="true" />
              )}
            </button>
          </div>

          <div role="grid" className="grid grid-cols-7 gap-y-0.5 text-center">
            {weekdays.map((day, index) => (
              <div
                key={index}
                role="columnheader"
                className="pb-1 text-[11px] font-medium text-[var(--color-muted)]"
              >
                {day}
              </div>
            ))}
            {Array.from({ length: leading }, (_, index) => (
              <div key={`pad-${index}`} aria-hidden="true" />
            ))}
            {days.map((day) => {
              const isSelected = selected !== undefined && isSameDay(day, selected)
              const isToday = isSameDay(day, today)
              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  role="gridcell"
                  disabled={outOfRange(day)}
                  aria-selected={isSelected}
                  onClick={() => pick(day)}
                  className={cn(
                    'mx-auto grid size-8 place-items-center rounded-[6px] text-sm tabular-nums',
                    'text-[var(--color-body)] hover:bg-[var(--color-primary-soft)]',
                    'focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]',
                    'disabled:pointer-events-none disabled:opacity-30',
                    !isSameMonth(day, month) && 'text-[var(--color-muted)]',
                    isToday && !isSelected && 'font-semibold text-[var(--color-primary)]',
                    isSelected &&
                      'bg-[var(--color-primary)] font-semibold text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)]',
                  )}
                >
                  {format(day, 'd', { locale })}
                </button>
              )
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
})
